import ReactDOM from 'react-dom';
import { FC, useEffect } from 'react';
import React from 'react';
import axios from 'axios';
import { Chart, registerables } from 'chart.js';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import ReactGA from 'react-ga4';
import { setScrubbedPage } from './app/routing/GAListner';

import 'url-search-params-polyfill';
// import { gapi } from 'gapi-script';

// Apps
//import { MetronicI18nProvider } from './_metronic/i18n/Metronici18n';
/**
 * TIP: Replace this style import with dark styles to enable dark mode
 *
 * import './_metronic/assets/sass/style.dark.scss'
 *
 * TIP: Replace this style import with rtl styles to enable rtl mode
 *
 * import './_metronic/assets/css/style.rtl.css'
 **/
import './_metronic/assets/sass/style.scss';
import './_metronic/assets/sass/style.react.scss';
import './react-datepicker.css'; // temporary fix for react-datepicker due to old webpack
import 'react-day-picker/dist/style.css';
import { AppRoutes } from './app/routing/AppRoutes';
import { AuthProvider, setupAxios } from './app/modules/auth';
import {
  get_deployment,
  get_google_client_id,
  get_long_version,
  is_dev,
  is_production,
  should_monitor,
} from './app/helpers/environment';
import * as Sentry from '@sentry/react';
import { transitions, positions, Provider as AlertProvider } from 'react-alert';
import { Alert } from 'react-bootstrap';
import { I18nProvider } from './_metronic/i18n/i18nProvider';
import { TimezoneProvider } from './app/context/TimezoneContext';
import { VersionUpdateBanner } from './app/components/VersionUpdateBanner';
import { installChunkRecovery } from './app/helpers/chunkRecovery';
import {
  REPLAY_OPTIONS,
  sentryBeforeBreadcrumb,
  sentryBeforeSend,
} from './app/helpers/monitoring';

// Fix for missing <dialog> polyfill in old IE and Safari (for google maps, used in AddressAutocomplete)
// import 'dialog-polyfill/dist/dialog-polyfill.css';
// import dialogPolyfill from 'dialog-polyfill';
// import 'core-js/actual';

// Register the polyfill
// if (typeof HTMLDialogElement === 'undefined') {
//   dialogPolyfill.registerDialog(window.document.createElement('dialog'));
// }

if (should_monitor() || process.env.REACT_APP_FORCE_MONITORING) {
  Sentry.init({
    dsn: 'https://b6cf2432836b45d58d30ce3b30c2e33e@o4503905078018048.ingest.sentry.io/4503905079132160',
    integrations: [
      Sentry.browserTracingIntegration({
        tracingOrigins: [
          'api.wiztherapy.com',
          'api-staging.wiztherapy.com',
          'api.wiz42.com',
          'api-staging.wiz42.com',
          'localhost',
        ],
      }),
      // Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration(REPLAY_OPTIONS),
      Sentry.feedbackIntegration({
        autoInject: false, // shown to signed-in providers only: monitoringSyncFeedbackWidget
        buttonLabel: 'Feedback',
        submitButtonLabel: 'Send Feedback',
        formTitle: 'Send Feedback',
        messagePlaceholder: 'Provide your feedback? Report a bug? Request a feature?',
        successMessageText: 'Thank you for your feedback!',
        showName: false,
        showEmail: false,
        useSentryUser: {
          email: 'email',
          name: 'name',
        },
        showBranding: false,
      }),
    ],
    release: get_long_version(),
    environment: get_deployment(),
    replaysSessionSampleRate: is_production() ? 0.2 : 0,
    replaysOnErrorSampleRate: 1.0,

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: is_production() ? 0.5 : 0,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/api\.wiztherapy\.com\/api/,
      /^https:\/\/api-staging\.wiztherapy\.com\/api/,
    ],
    profilesSampleRate: is_production() ? 0.5 : 0,
    // No BAA with Sentry: mask emails, drop payloads and query strings before anything leaves.
    beforeSend: sentryBeforeSend,
    beforeSendTransaction: sentryBeforeSend,
    beforeBreadcrumb: sentryBeforeBreadcrumb,
  });
  // Replay events (their visited urls) skip beforeSend but pass event processors.
  Sentry.addEventProcessor((event) => (event.type === 'replay_event' ? sentryBeforeSend(event) : event));
} else {
  console.error('Running dev app - monitoring disabled');
}

// WST-865: a session left open across a deploy asks for chunks the server has already
// deleted. Register before React renders so a failure during initial hydration is caught,
// and after Sentry.init so Sentry is already listening when we reload.
installChunkRecovery();

try {
  const deployment = get_deployment();
  const Ga4Ids = { staging: 'G-7Y1ZKRWCT2', dev: 'G-EKM8H0660J', production: 'G-G7VLD8X57L' };
  // GAListener sends each page view with a scrubbed URL; the automatic one would carry the raw URL.
  ReactGA.initialize(Ga4Ids[deployment], { gtagOptions: { send_page_view: false } });
  setScrubbedPage();
} catch {
  console.error('Failed to setup GA4');
}

const urlParams = new URLSearchParams(window.location.search);

// if (!is_dev()) {
//   gapi.load('client:auth2', () => {
//     gapi.client.init({
//       clientId: get_google_client_id(),
//       plugin_name: 'chat',
//     });
//   });
// }

/**
 * Creates `axios-mock-adapter` instance for provided `axios` instance, add
 * basic Metronic mocks and returns it.
 *
 * @see https://github.com/ctimmerm/axios-mock-adapter
 */

/**
 * Inject Metronic interceptors for axios.
 *
 * @see https://github.com/axios/axios#interceptors
 */
const magicToken = urlParams.get('magic');
setupAxios({ axios, magicToken });

Chart.register(...registerables);

const queryClient = new QueryClient();

const alertOptions = {
  position: positions.BOTTOM_CENTER,
  timeout: 5000,
  offset: '5px',
  transition: transitions.SCALE,
};

const WtAlertTemplate = (props: any) => {
  const variant = props.options.type === 'error' ? 'danger' : props.options.type;
  return (
    <Alert variant={variant} className="mb-5" style={{ ...props.style }} dismissible onClick={props.close}>
      <span style={{ flex: 2 }}>{props.message}</span>
    </Alert>
  );
};

ReactDOM.render(
  <AlertProvider
    template={WtAlertTemplate}
    {...alertOptions}
    containerStyle={{
      zIndex: 9999999,
      position: 'fixed',
    }}
  >
    <VersionUpdateBanner />
    <QueryClientProvider client={queryClient}>
      {/* <MetronicI18nProvider> */}
      {/* <I18nProvider> */}
      <AuthProvider>
        <TimezoneProvider>
          <AppRoutes />
        </TimezoneProvider>
      </AuthProvider>
      {/* </I18nProvider> */}

      {/* </MetronicI18nProvider> */}
      {process.env.REACT_APP_QUERY_DEBUG === 'true' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </AlertProvider>,
  document.getElementById('root')
);
